import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";

/**
 * Custom hook to synchronize return schedule state to URL query parameters.
 *
 * @param hydratedRef - Ref tracking whether initial hydration has completed
 *
 * @remarks
 * - Only syncs after initial hydration to avoid overwriting URL on mount
 * - Uses refs to prevent redundant URL updates
 * - Syncs returnDate, returnTime, and returnDays parameters
 * - Uses replace navigation to avoid polluting browser history
 *
 * @example
 * ```tsx
 * function ReturnSchedulePage() {
 *   const hydratedRef = useRef(false);
 *   useReturnScheduleHydration(); // sets hydratedRef
 *   useReturnScheduleUrlSync(hydratedRef);
 * }
 * ```
 */
export function useReturnScheduleUrlSync(hydratedRef: React.MutableRefObject<boolean>) {
  const location = useLocation();
  const navigate = useNavigate();

  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);

  const lastWrittenReturnDateRef = useRef<string | null>(null);
  const lastWrittenReturnTimeRef = useRef<string | null>(null);
  const lastWrittenReturnDaysRef = useRef<string | null>(null);

  // Sync returnDate parameter
  useEffect(() => {
    if (!hydratedRef.current) return; // Wait for initial hydration
    if (!orderData.keyReturnFromDate) return;

    const dateStr = orderData.keyReturnFromDate.format("YYYY-MM-DD");
    if (lastWrittenReturnDateRef.current === dateStr) return; // Prevent redundant writes

    const params = new URLSearchParams(location.search);
    const currentDate = params.get("returnDate");

    if (currentDate === dateStr) {
      lastWrittenReturnDateRef.current = dateStr;
      return;
    }

    // Remove old parameter names if they exist
    params.delete("returnFromDate");
    params.delete("returnToDate");
    
    params.set("returnDate", dateStr);
    lastWrittenReturnDateRef.current = dateStr;

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.keyReturnFromDate, hydratedRef, location.pathname, navigate]);

  // Sync returnTime parameter
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!orderData.keyReturnFromDate) return;

    const timeStr = orderData.keyReturnFromDate.format("HH:mm");
    if (lastWrittenReturnTimeRef.current === timeStr) return;

    const params = new URLSearchParams(location.search);
    const currentTime = params.get("returnTime");

    if (currentTime === timeStr) {
      lastWrittenReturnTimeRef.current = timeStr;
      return;
    }

    // Remove old parameter names if they exist
    params.delete("returnFromTime");
    params.delete("returnTimeFrom");
    
    params.set("returnTime", timeStr);
    lastWrittenReturnTimeRef.current = timeStr;

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.keyReturnFromDate, hydratedRef, location.pathname, navigate]);

  // Sync returnDays parameter
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!orderData.keyReturnFromDate || !orderData.keyReturnToDate) return;

    const numberOfDays = orderData.keyReturnToDate.diff(orderData.keyReturnFromDate, "days");
    const daysStr = numberOfDays.toString();

    if (lastWrittenReturnDaysRef.current === daysStr) return;

    const params = new URLSearchParams(location.search);
    const currentDays = params.get("returnDays");

    if (currentDays === daysStr) {
      lastWrittenReturnDaysRef.current = daysStr;
      return;
    }

    // Remove old parameter name if it exists
    params.delete("returnTimeTo");
    params.delete("returnToTime");
    
    params.set("returnDays", daysStr);
    lastWrittenReturnDaysRef.current = daysStr;

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.keyReturnFromDate, orderData.keyReturnToDate, hydratedRef, location.pathname, navigate]);
}
